// Зеркало серверного расчёта (course-service SalaryCalculator) для предпросмотра.
// Итоговый расчёт всегда подтверждается на бэкенде (ТЗ §9.3).
import { SalaryConfigEntry } from '../features/salary/salarySlice'
import { tone } from '../utils/uiTokens'

export const SALARY_DEFAULTS: Record<string, number> = {
    VIDEO_GATE: 65, VIDEO_FULL: 90, VIDEO_MAX: 1500,
    TEST_GATE: 60, TEST_FULL: 85, TEST_MAX: 400,
    PS_GATE: 50, PS_FULL: 85, PS_MAX: 400, PS_SESSIONS: 8,
    ZACHET_GATE: 75, ZACHET_MAX: 400, ZACHET_WEEKS: 4,
    OPROS_MAX: 300,
    OPROS_S1_MIN: 85, OPROS_S1_AMT: 265,
    OPROS_S2_MIN: 90, OPROS_S2_AMT: 280,
    OPROS_S3_MIN: 95, OPROS_S3_AMT: 290,
    OPROS_S4_MIN: 99, OPROS_S4_AMT: 300,
    PER_STUDENT_MAX: 3000,
}

export type SalaryConfig = Record<string, number>

export const toConfig = (entries?: SalaryConfigEntry[]): SalaryConfig => {
    const cfg = { ...SALARY_DEFAULTS }
    entries?.forEach((e) => {
        cfg[e.key] = e.value
    })
    return cfg
}

const c = (cfg: SalaryConfig, key: string) => (cfg[key] ?? SALARY_DEFAULTS[key] ?? 0)

export const payVideo = (pct: number, cfg: SalaryConfig = SALARY_DEFAULTS) =>
    pct < c(cfg, 'VIDEO_GATE') ? 0
        : pct > c(cfg, 'VIDEO_FULL') ? c(cfg, 'VIDEO_MAX')
            : Math.round((c(cfg, 'VIDEO_MAX') * pct) / 100)

export const payTest = (pct: number, cfg: SalaryConfig = SALARY_DEFAULTS) =>
    pct < c(cfg, 'TEST_GATE') ? 0
        : pct >= c(cfg, 'TEST_FULL') ? c(cfg, 'TEST_MAX')
            : Math.round((c(cfg, 'TEST_MAX') * pct) / 100)

export const payPs = (pct: number, cfg: SalaryConfig = SALARY_DEFAULTS) =>
    pct < c(cfg, 'PS_GATE') ? 0
        : pct >= c(cfg, 'PS_FULL') ? c(cfg, 'PS_MAX')
            : Math.round((c(cfg, 'PS_MAX') * pct) / 100)

export const payZachet = (pct: number, cfg: SalaryConfig = SALARY_DEFAULTS) =>
    pct < c(cfg, 'ZACHET_GATE') ? 0 : Math.round((c(cfg, 'ZACHET_MAX') * pct) / 100)

export const payOpros = (pct: number, cfg: SalaryConfig = SALARY_DEFAULTS) =>
    pct < c(cfg, 'OPROS_S1_MIN') ? 0
        : pct < c(cfg, 'OPROS_S2_MIN') ? c(cfg, 'OPROS_S1_AMT')
            : pct < c(cfg, 'OPROS_S3_MIN') ? c(cfg, 'OPROS_S2_AMT')
                : pct < c(cfg, 'OPROS_S4_MIN') ? c(cfg, 'OPROS_S3_AMT')
                    : c(cfg, 'OPROS_S4_AMT')

export const money = (n?: number) =>
    `${Math.round(n ?? 0).toLocaleString('ru-RU').replace(/ /g, ' ')} ₸`

// Порядок и метаданные факторов для UI (ключи совпадают с бэкендом)
export const FACTOR_KEYS = ['VIDEO', 'TEST', 'PS', 'ZACHET', 'OPROS'] as const
export type FactorKey = (typeof FACTOR_KEYS)[number]

// Статус-тон по доле начисления: полностью / частично / ноль
export type Tone = 'ok' | 'warn' | 'bad'

export const toneOf = (amount: number, max: number): Tone =>
    amount >= max - 0.5 ? 'ok' : amount > 0 ? 'warn' : 'bad'

export const TONE_COLORS: Record<Tone, string> = {
    ok: tone.ok.text,
    warn: tone.warn.text,
    bad: tone.bad.text,
}

export const TONE_SOFT: Record<Tone, string> = {
    ok: tone.ok.bg,
    warn: tone.warn.bg,
    bad: tone.bad.bg,
}

// Метаданные факторов для UI: i18n-ключи названия и правила расчёта.
export const FACTOR_META: { key: FactorKey; labelId: string; ruleId: string; maxKey: string }[] = [
    { key: 'VIDEO', labelId: 'factorVideo', ruleId: 'factorVideoRule', maxKey: 'VIDEO_MAX' },
    { key: 'TEST', labelId: 'factorTest', ruleId: 'factorTestRule', maxKey: 'TEST_MAX' },
    { key: 'PS', labelId: 'factorPs', ruleId: 'factorPsRule', maxKey: 'PS_MAX' },
    { key: 'ZACHET', labelId: 'factorZachet', ruleId: 'factorZachetRule', maxKey: 'ZACHET_MAX' },
    { key: 'OPROS', labelId: 'factorOpros', ruleId: 'factorOprosRule', maxKey: 'OPROS_MAX' },
]
